import React, { FunctionComponent, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import {
  ButtonDropdown, Col, DropdownItem, DropdownMenu, DropdownToggle, Row, Button,
} from 'reactstrap';
import moment from 'moment';
import ComplexTable from '../../components/Table/ComplexTable';
import { IUserState } from '../../store/user/types';
import Section from '../../components/Section';
import SearchInput from '../../components/Inputs/SearchInput';
import { ITicketsState } from '../../store/tickets/types';
import {
  getAccountTickets,
  setTicketsSearchString,
  toggleAccountTicketsLoading,
  toggleNewTicketsDropdown,
  resetTicketsStore,
  toggleTicketsFilterDropdown,
  setTicketsFilter,
} from '../../store/tickets/actions';
import { checkPermission } from '../../utils/Auth/AuthService';

import SearchIcon from '../../components/SvgIcons/SearchIcon';
import SimpleLoader from '../../components/SimpleLoader';
import Refresh from '../../components/SvgIcons/Refresh';
import { ACCOUNT_TYPE_RESALE_PARTNER, IAccountState } from '../../store/account/types';
import CloudDown from '../../components/SvgIcons/CloudDown';
import { AppDispatch } from '../../configureStore';
import { escapeCsvFormulaRows } from '../../helpers/csv';

type TOwnProps = {
  user: IUserState;
  tickets: ITicketsState;
  dispatch: AppDispatch;
  account: IAccountState;
};
type TProps = TOwnProps;
const Tickets: FunctionComponent<TProps> = (props) => {

  const { dispatch, account } = props;
  const {
    tickets, loading, searchString, openNewTicketDropdown, openFilterTicketsDropdown, visibleState,
  } = props.tickets;
  const { user } = props;
  const navigate = useNavigate();

  useEffect(() => {

    if (tickets.length === 0 || account.accountTypeCode === ACCOUNT_TYPE_RESALE_PARTNER) refreshStore();

  }, []);
  const refreshStore = () => {

    dispatch(resetTicketsStore());
    dispatch(toggleAccountTicketsLoading());
    dispatch(getAccountTickets()).then(() => dispatch(toggleAccountTicketsLoading()));

  };
  const onChangeSearch = (e) => {

    const searchString = e.target.value.toString();
    dispatch(setTicketsSearchString(searchString));

  };
  const toggleNewTicket = () => {

    dispatch(toggleNewTicketsDropdown());

  };
  const togglFilterTickets = () => {

    dispatch(toggleTicketsFilterDropdown());

  };

  const filtered = (item) => String(item.title).toLowerCase().indexOf(searchString.toLowerCase()) !== -1
    || String(item.ticketNumber).toLowerCase().indexOf(searchString.toLowerCase()) !== -1
    || String(item.state).toLowerCase().indexOf(searchString.toLowerCase()) !== -1
    || String(item.status).toLowerCase().indexOf(searchString.toLowerCase()) !== -1
    || String(item.createdOn).toLowerCase().indexOf(searchString.toLowerCase()) !== -1
    || String(item.modifiedOn).toLowerCase().indexOf(searchString.toLowerCase()) !== -1;

  const getVisibleTickets = () => {
    const isFiltered = (theTicket) => filtered(theTicket);

    const statusConditions = {
      Canceled: (theTicket) => theTicket.status === 'Canceled',
      Active: (theTicket) => theTicket.status === 'Active',
      Resolved: (theTicket) => theTicket.status === 'Resolved',
      All: (theTicket) => theTicket.status !== 'Merged',
    };

    const visibleTickets = tickets.filter((theTicket) => {
      const condition = statusConditions[visibleState];

      if (condition) {
        return searchString ? condition(theTicket) && isFiltered(theTicket) : condition(theTicket);
      }
      return true;
    });

    return visibleTickets;
  };
  const columns = [
    {
      Header: 'Ticket Number',
      accessor: 'ticketNumber',
      width: 150,
    },
    {
      Header: 'Title',
      accessor: 'title',
      minWidth: 500,
    },
    {
      Header: 'Status',
      accessor: 'status',
    },
    {
      Header: 'Status Reason',
      accessor: 'statusReason',
    },
    {
      Header: 'Date Created',
      accessor: 'createdOn',
      Cell: (props) => (props.value ? moment(props.value).format('DD/MM/YYYY') : <></>),
    },
    {
      Header: 'Date Modified',
      accessor: 'modifiedOn',
      Cell: (props) => (props.value ? moment(props.value).format('DD/MM/YYYY') : <></>),
    },
    {
      Header: '',
      filterable: false,
      sortable: false,
      accessor: 'id',
      width: 50,
      Cell: (props) => (
        <Link className="grey-icon rounded" to={`/tickets/${props.original.ticketNumber}`}>
          <SearchIcon title="details" />
        </Link>
      ),
    },
  ];
  return (
    <div className="animated fadeIn mb-3">
      <Section title="Tickets">
        <Row className="mb-4">
          <Col className="d-flex flex-row" md={6}>
            <SearchInput value={searchString} onChange={onChangeSearch} />
            <ButtonDropdown isOpen={openFilterTicketsDropdown} toggle={togglFilterTickets} className="ml-2 d-flex justify-content-space-between">
              <DropdownToggle caret color="primary">
                <span className="d-inline-block" style={{ width: '70px' }}>{visibleState !== '' ? visibleState : 'All'}</span>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => {

                  props.dispatch(setTicketsFilter('Resolved'));

                }}
                >
                  Resolved
                </DropdownItem>
                <DropdownItem onClick={() => {

                  props.dispatch(setTicketsFilter('Active'));

                }}
                >
                  Active
                </DropdownItem>
                <DropdownItem onClick={() => {

                  props.dispatch(setTicketsFilter('Canceled'));

                }}
                >
                  Canceled
                </DropdownItem>
                <DropdownItem onClick={() => {

                  props.dispatch(setTicketsFilter('All'));

                }}
                >
                  All
                </DropdownItem>
              </DropdownMenu>
            </ButtonDropdown>
          </Col>
          <Col md="6" className="d-flex justify-content-end">
            {checkPermission('cases.write', user.permissions)
              && (
                <ButtonDropdown isOpen={openNewTicketDropdown} toggle={toggleNewTicket} className="mr-2">
                  <DropdownToggle caret color="primary">
                    New Ticket
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => {

                      navigate('/tickets/new/generic');

                    }}
                    >
                      Generic Ticket
                    </DropdownItem>
                    <DropdownItem header>Fault Reporting</DropdownItem>
                    <DropdownItem onClick={() => {

                      navigate('/tickets/new/adslfault');

                    }}
                    >
                      ADSL Fault
                    </DropdownItem>
                    <DropdownItem onClick={() => {

                      navigate('/tickets/new/voicefault');

                    }}
                    >
                      Voice Fault
                    </DropdownItem>
                    <DropdownItem header>Change Requests</DropdownItem>
                    <DropdownItem onClick={() => {

                      navigate('/tickets/new/voicechange');

                    }}
                    >
                      Voice Change
                    </DropdownItem>
                    <DropdownItem onClick={() => {

                      navigate('/tickets/new/vpn');

                    }}
                    >
                      New VPN
                    </DropdownItem>
                    <DropdownItem onClick={() => {

                      navigate('/tickets/new/change');

                    }}
                    >
                      Other Change
                    </DropdownItem>
                  </DropdownMenu>
                </ButtonDropdown>
              )}
            <CSVLink className="pt-1 px-1 btn btn-warning" color="warning" data={escapeCsvFormulaRows(getVisibleTickets())} filename="tickets.csv">
              <CloudDown cssClassTop="refresh-svg" title="Download as CSV" />
            </CSVLink>
            <Button className="pt-1 px-1 ml-2" color="primary" onClick={() => refreshStore()}>
              <Refresh title="Refresh" cssClassTop="refresh-svg" />
            </Button>
          </Col>
        </Row>
        <Row className="mt-2 mr-0 ml-0">
          <Col className="p-4 bg-white rounded">
            <SimpleLoader loading={loading}>
              <ComplexTable
                columns={columns}
                data={getVisibleTickets()}
                defaultSorted={{
                  id: 'modifiedOn',
                  desc: true,
                }}
              />
            </SimpleLoader>
          </Col>
        </Row>
      </Section>
    </div>
  );

};
function mapStateToProps({ user, tickets, account }) {

  return {
    tickets,
    user,
    account,
  };

}
export default connect(mapStateToProps)(Tickets);
